import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { GetReturnTypeFromApiSpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dyn, dynVariadic, scheduleForCleanup } from "trader.ts/ui";
import { renderIf } from "trader.ts/ui.common";
import {
  constantField,
  dateBox,
  Field,
  Form,
  Parsing,
  selectBox,
  textBox,
  timeBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled, mapPartial } from "trader.ts/utils";
import { match, P } from "ts-pattern";
import {
  enrollmentid,
  formulaid,
  instrumentid,
  internalteacherid,
  lesson_location,
  lessongroupid,
  roomid,
  teacherid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import { radioField } from "../../client/radiofield";
import { renderSaveButton } from "../../client/savebutton";
import { weekField } from "../../client/weekfield";
import { formulaFull } from "../../domain/config";
import { period } from "../../domain/periods";
import { green, purple, red } from "../../lib/colors";
import { groupByIntoArrays } from "../../lib/groupbyintoarrays";
import { isNil } from "../../lib/istype";
import { replaceAt } from "../../lib/replaceat";
import { allWeekdays } from "../../lib/time";
import { parseWeekday } from "../../parsing_from_ur";
import {
  ddMMM,
  renderEnrollmentrequestStatus,
  renderFirstNameLastName,
  renderTeacherPlusTimePlusWeekday,
  renderTimePlusWeekday,
  wrapInParentheses,
} from "../../render";
import * as routes from "../../routes";
import { renderPeriod, translateWeekday } from "../../translations_specific";
import {
  errorField,
  formulaField,
  locationField,
} from "../planning/lessongroupform";

type reenrollment_poss_from_server = GetReturnTypeFromApiSpec<
  typeof routes.queryReenrollmentsSpec
>[number];

type lessongroup_reenrollments = {
  lessongroupid: lessongroupid;
  enrollmentids: enrollmentid[];
  time: joda.LocalTime;
  weekday: joda.DayOfWeek;
  instrumentid: instrumentid;
  lessonduration: number;
  isTrialLessonExtension: boolean;
  formulaid: formulaid;
  teacher: {
    internalteacherid: internalteacherid;
    id: teacherid;
    firstname: string;
    lastname: string;
  };
  location: {
    location: lesson_location;
    roomid: roomid | null;
  };
};

type reenrollmentGroup = {
  lessongroupid: lessongroupid;
  isTrialLessonExtension: boolean;
  fromServer: reenrollment_poss_from_server[];
  form: Field<{ values: Omit<lessongroup_reenrollments, "enrollmentids"> }>;
  selectedEnrIds: Source<enrollmentid[]>;
};

type query =
  | {
      tag: "daterange";
      dates: {
        firstday: joda.LocalDate;
        lastday: joda.LocalDate;
      };
    }
  | {
      tag: "end_of_period";
      ending_period: period;
      next_period: period;
    };

export function render(opts: {
  mode: "daterange" | "end_of_period";
  period: period;
  nextPeriod: null | period;
  internalteacherid: null | internalteacherid;
  allFormulas: formulaFull[];
  allInstruments: { description: string; id: instrumentid }[];
  allInternalTeachers: {
    id: teacherid;
    firstname: string;
    lastname: string;
    internalteacherid: internalteacherid;
    instruments: { id: instrumentid; description: string }[] | null;
  }[];
}): HTMLElement | HTMLElement[] {
  const mode = opts.mode;
  const today = joda.LocalDate.now();

  const savedS: Source<null | GetReturnTypeFromApiSpec<
    typeof routes.execReenrollmentsSpec
  >> = new Source(null);

  const hackReloadReenrollmentsS = new Source(false);
  const actionFormExpandedS = new Source(false);

  function reload() {
    hackReloadReenrollmentsS.set(!hackReloadReenrollmentsS.get());
  }

  const form = new Form({})
    .addField(
      "query",
      [],
      async (): Promise<
        Field<
          | {
              tag: "daterange";
              dates: { firstday: joda.LocalDate; lastday: joda.LocalDate };
            }
          | { tag: "end_of_period"; ending_period: period; next_period: period }
        >
      > => {
        console.log(mode);
        if (mode === "daterange") {
          return new Form({})
            .addField("tag", [], () => constantField("daterange" as const))
            .addField("dates", [], () => {
              return weekField({
                initialVal: [today, opts.period.firstday].maxBy((d) =>
                  d.toEpochDay(),
                )!,
                firstday: opts.period.firstday,
                lastday: opts.period.lastday,
                label: "Laatste les in week",
                isDisabled: (w) => w.lastday.isBefore(today.minusWeeks(2)),
                saveAndLoadInitialValToLocalStorage:
                  "reenrollments_daterange_week",
              });
            })
            .build();
        } else if (mode === "end_of_period") {
          return new Form({})
            .addField("tag", [], () => constantField("end_of_period" as const))
            .addField("ending_period", [], (): Promise<Field<period>> => {
              if (opts.nextPeriod !== null) {
                return constantField(opts.period); // !
              } else {
                return errorField(
                  <div>
                    <div>
                      {"Je hebt nog geen nieuw schooljaar aangemaakt, dus je kan ook nog geen herinschrijvingen voor het nieuwe schooljaar aanmaken."}
                    </div>
                    <a href={routes.config_periods_listRoute.link()}>
                      {"Maak hier een nieuw schooljaar aan."}
                    </a>
                  </div>,
                );
              }
            })
            .addField("next_period", [], (): Promise<Field<period>> => {
              if (opts.nextPeriod !== null) {
                return constantField(opts.nextPeriod); // !
              } else {
                return errorField(<span></span>);
              }
            })
            .build();
        } else {
          return checkAllCasesHandled(mode);
        }
      },
    )
    .addField("teacher_", [], async () => {
      const values = [
        {
          label: "Allemaal",
          values: opts.allInternalTeachers,
        },
        ...opts.allInternalTeachers
          .orderBy(renderFirstNameLastName)
          .map((t) => ({
            label: renderFirstNameLastName(t),
            values: [t],
          })),
      ];
      return selectBox({
        values: values,
        initial: values[0],
        saveAndLoadInitialValToLocalStorage: "reenrollments_teacher",
        show: (t) => t.label,
        label: "Docent",
      });
    })
    .addField("teachers", ["teacher_"], async ({ teacher_ }) =>
      constantField(teacher_.values),
    )
    .addField("instrument_", [], async () => {
      const values = [
        {
          label: "Allemaal",
          values: opts.allInstruments,
        },
        ...opts.allInstruments.map((t) => ({
          label: t.description,
          values: [t],
        })),
      ];
      return selectBox({
        values: values,
        initial: values[0],
        saveAndLoadInitialValToLocalStorage: "reenrollments_instrument",
        show: (t) => t.label,
        label: "Cursus",
      });
    })
    .addField("instruments", ["instrument_"], async ({ instrument_ }) =>
      constantField(instrument_.values),
    )
    .addField("weekday_", [], async () => {
      const values = [
        {
          label: "Allemaal",
          values: allWeekdays,
        },
        ...allWeekdays.map((t) => ({
          label: translateWeekday(t),
          values: [t],
        })),
      ];
      return selectBox({
        values: values,
        initial: values[0],
        saveAndLoadInitialValToLocalStorage: "reenrollments_weekday",
        show: (t) => t.label,
        label: "Weekdag",
      });
    })
    .addField("weekdays", ["weekday_"], async ({ weekday_ }) =>
      constantField(weekday_.values),
    )
    .addField(
      "queryParams",
      ["query", "teachers", "weekdays", "instruments"],
      async ({ query, teachers, weekdays, instruments }) => {
        const field = await constantField(calc());

        scheduleForCleanup(
          hackReloadReenrollmentsS.observe(() => {
            field.s.set({ tag: "parsed", parsed: calc() });
          }),
        );

        function calc() {
          return {
            internalteacherids: (teachers.length === 0
              ? opts.allInternalTeachers
              : teachers
            ).map((t) => t.internalteacherid),
            instrumentids: (instruments.length === 0
              ? opts.allInstruments
              : instruments
            ).map((i) => i.id),
            weekdays: weekdays.length === 0 ? allWeekdays : weekdays,
            query:
              query.tag === "end_of_period"
                ? {
                    tag: "end_of_period" as const,
                    ending_period_id: query.ending_period.id,
                    ending_period: query.ending_period,
                    next_period: query.next_period,
                  }
                : { tag: "daterange" as const, dates: query.dates },
          };
        }
        return field;
      },
    )
    .addField(
      "reenrollments",
      ["queryParams"],
      async ({ queryParams }): Promise<Field<lessongroup_reenrollments[]>> => {
        const reenrollments = await rpc(
          routes.queryReenrollmentsSpec,
          undefined,
          queryParams,
        );

        const parsingS: Source<Parsing<lessongroup_reenrollments[]>> =
          new Source({
            tag: "parsed",
            parsed: [],
          });

        const groupedReenrollments: reenrollmentGroup[] = groupByIntoArrays(
          reenrollments,
          (rr) =>
            rr.lessongroup.id.toString() +
            (rr.status.tag === "AvailableGrouplesson" &&
            // We want to seperate trial lessons and non-trial lessons
            // Trial lessons are reenrolled into the current LG
            // Non-trial lessons are reenrolled into a new LG
            rr.status.isTrialLessonExtension
              ? "trial"
              : ""),
        )
          .orderBy([
            ([_, g]) => renderFirstNameLastName(g.first()!.teacher),
            ([_, g]) => parseWeekday(g.first()!.lessongroup.weekday).ordinal(),
            ([_, g]) => g.first()!.lessongroup.time.toSecondOfDay(),
          ])
          .map(([_, g]) => {
            const first = g.first()!;
            const isTrialLessonExtension =
              first.status.tag === "AvailableGrouplesson" &&
              first.status.isTrialLessonExtension;
            const selectedEnrIds = new Source([]);
            const form = makeLessongroupEditForm(
              queryParams.query,
              opts,
              selectedEnrIds,
              makeLgEditFormInitialFromServerStruct(
                g.first()!,
                isTrialLessonExtension,
              ),
            );

            return {
              lessongroupid: g.first()!.lessongroup.id,
              fromServer: g,
              isTrialLessonExtension,
              form,
              selectedEnrIds,
            };
          });

        recalcTotal(null);
        function recalcTotal(i: number | null) {
          const acc: {
            tag: "parsed";
            parsed: lessongroup_reenrollments[];
          } = { tag: "parsed" as const, parsed: [] };
          const curr = parsingS.get();
          const updatedGroup = !isNil(i) ? groupedReenrollments[i] : null;
          const updatedGroupFormS = updatedGroup?.form.s.get() || null;
          if (
            !isNil(i) &&
            updatedGroup &&
            updatedGroupFormS &&
            updatedGroupFormS.tag === "parsed" &&
            curr.tag === "parsed"
          ) {
            // optimalisation: just replace the updated group
            parsingS.set({
              tag: "parsed",
              parsed: replaceAt(curr.parsed, i, {
                ...updatedGroupFormS.parsed.values,
                enrollmentids: updatedGroup.selectedEnrIds.get(),
              }),
            });
          } else {
            for (const g of groupedReenrollments) {
              const p = g.form.s.get();
              if (p.tag === "parsed") {
                acc.parsed.push({
                  ...p.parsed.values,
                  enrollmentids: g.selectedEnrIds.get(),
                });
              } else if (p.tag === "loading") {
                return parsingS.set({ tag: "loading" });
              } else if (p.tag === "err") {
                return parsingS.set({ tag: "err", label: p.label });
              } else if (p.tag === "initial") {
                return parsingS.set({ tag: "initial" });
              } else {
                checkAllCasesHandled(p);
              }
            }
            parsingS.set(acc);
          }
        }

        return {
          render: () => {
            groupedReenrollments.forEach((g, i) => {
              scheduleForCleanup(g.form.s.observe(() => recalcTotal(i)));
              scheduleForCleanup(
                g.selectedEnrIds.observe(() => recalcTotal(i)),
              );
            });

            return [
              renderReenrollmentsSummary(reenrollments),
              renderReenrollments(reload, parsingS, groupedReenrollments, opts),
            ];
          },
          s: parsingS,
          cleanup: () => {},
        };
      },
    )
    .addField(
      "action",
      ["queryParams", "reenrollments"],
      async ({ queryParams, reenrollments }) => {
        const query = queryParams.query;

        const selectedN = reenrollments.flatMap(
          (lg) => lg.enrollmentids,
        ).length;

        return new Form({})
          .addField("actionChoice", [], async () =>
            radioField({
              initial: "make_reservation" as const,
              values: [
                "make_invitation" as const,
                "make_reservation" as const,
                "make_enrollment" as const,
                "not_to_be_reenrolled" as const,
              ],
              show: function (t): string {
                if (t === "make_invitation") {
                  return "Maak uitnodigingen";
                } else if (t === "make_reservation") {
                  return "Maak reservaties";
                } else if (t === "make_enrollment") {
                  return "Maak inschrijvingen";
                } else if (t === "not_to_be_reenrolled") {
                  return "Worden niet verlengd";
                } else {
                  return checkAllCasesHandled(t);
                }
              },
              containerClass: style.panel_tabs,
              saveAndLoadInitialValToLocalStorage:
                "reenrollments_action_choice",
            }),
          )
          .addField(
            "actionChoiceInformational",
            ["actionChoice"],
            ({ actionChoice }) => {
              function renderActionChoiceInformation() {
                const makeCard = (ss: string[]) => (
                  <article
                    style=" font-style: italic; padding: 0px 0px 16px 16px; border-bottom: 1px solid var(--grey-200); margin: 16px -16px 16px; "
                    className={style.text_with_icon}
                  >
                    <ul style="list-style: disc; padding-left: 16px">
                      {ss.map((s) => (
                        <li>{s}</li>
                      ))}
                    </ul>
                  </article>
                );
                if (actionChoice === "make_invitation") {
                  return makeCard([
                    "Zet emails in de wachtrij met een link om opnieuw in te schrijven.",
                  ]);
                } else if (actionChoice === "make_reservation") {
                  return makeCard([
                    "Zet emails in de wachtrij met een link om opnieuw in te schrijven.",
                    "Reserveert de tijdsloten voor deze studenten.",
                  ]);
                } else if (actionChoice === "make_enrollment") {
                  return makeCard([
                    "Maakt lessenreeksen aan",
                    "Maakt facturen en jaargelden aan indien nodig",
                    "Zet emails in wachtrij",
                  ]);
                } else if (actionChoice === "not_to_be_reenrolled") {
                  return makeCard([
                    "Markeert deze lessenreeksen als 'Wordt niet verlengd'",
                    "Deze lessenreeksen zullen niet meer geselecteerd kunnen worden voor herinschrijving",
                  ]);
                } else {
                  return checkAllCasesHandled(actionChoice);
                }
              }

              return constantFieldWithRender(
                0,
                renderActionChoiceInformation(),
              );
            },
          )
          .addField(
            "action",
            ["actionChoice"],
            async ({
              actionChoice,
            }): Promise<
              Field<
                | { tag: "make_invitation" }
                | { tag: "make_enrollment" }
                | { tag: "not_to_be_reenrolled"; reason: string }
                | { tag: "make_reservation"; reserved_until: joda.LocalDate }
              >
            > => {
              if (actionChoice === "make_invitation") {
                return new Form({})
                  .addField("tag", [], () =>
                    constantField("make_invitation" as const),
                  )
                  .build();
              } else if (actionChoice === "make_enrollment") {
                return new Form({})
                  .addField("tag", [], () =>
                    constantField("make_enrollment" as const),
                  )
                  .build();
              } else if (actionChoice === "not_to_be_reenrolled") {
                return new Form({})
                  .addField("tag", [], () =>
                    constantField("not_to_be_reenrolled" as const),
                  )
                  .addField("reason", [], () =>
                    textBox({ initialVal: "", label: "Reden" }),
                  )
                  .build();
              } else if (actionChoice === "make_reservation") {
                return new Form({})
                  .addField("tag", [], () =>
                    constantField("make_reservation" as const),
                  )
                  .addField("reserved_until", [], () =>
                    dateBox({
                      label: "Reserveer tot",
                      initialVal:
                        query.tag === "daterange"
                          ? query.dates.lastday.plusDays(8)
                          : query.ending_period.lastday,
                    }),
                  )
                  .build();
              } else {
                return checkAllCasesHandled(actionChoice);
              }
            },
          )
          .addField(
            "earliest_planning_date_choice",
            ["action"],
            async ({ action }): Promise<Field<"asap" | "week">> => {
              if (
                query.tag === "daterange" &&
                action.tag !== "not_to_be_reenrolled"
              ) {
                return radioField({
                  initial: "asap" as const,
                  values: ["asap" as const, "week" as const],
                  saveAndLoadInitialValToLocalStorage:
                    "reenrollments_earliest_planning_date_choice",
                  show: function (t): string {
                    if (t === "asap") {
                      return "Zo snel mogelijk";
                    } else if (t === "week") {
                      return "Vanaf bepaalde week";
                    } else {
                      return checkAllCasesHandled(t);
                    }
                  },
                  label: "Nieuwe inschrijvingen starten",
                  containerClass: style.radio_one_line,
                });
              } else {
                return constantField("asap");
              }
            },
          )
          .addField(
            "earliest_planning_date",
            ["earliest_planning_date_choice"],
            async ({
              earliest_planning_date_choice,
            }): Promise<
              Field<null | {
                index: number;
                firstday: joda.LocalDate;
                lastday: joda.LocalDate;
              }>
            > => {
              if (
                earliest_planning_date_choice === "week" &&
                query.tag === "daterange"
              ) {
                const weekF = await weekField({
                  initialVal: query.dates.firstday.plusDays(7),
                  firstday: opts.period.firstday,
                  lastday: opts.period.lastday,
                  label: "",
                  isDisabled: (w) => w.firstday.isBefore(query.dates.lastday),
                });
                return {
                  ...weekF,
                  render: () => (
                    <div style="max-width: 50%; margin-left: auto;">
                      {weekF.render()}
                      <article className={style.formsection_informational}>
                        <ul style="list-style: disc; padding-left: 16px;">
                          <li>
                            {"De nieuwe lessen zullen ten vroegste starten vanaf deze week"}
                          </li>
                          <li>
                            {"Je kan dit gebruiken om bvb: het tweede semester in je schooljaar af te bakenen"}
                          </li>
                        </ul>
                      </article>
                    </div>
                  ),
                };
              } else {
                return constantField(null);
              }
            },
          )
          .addField(
            "saveButton",
            ["action", "earliest_planning_date"],
            ({ action, earliest_planning_date }) => {
              // Adding this also inside the form, otherwise rendering inside the panel becomes complicated

              return constantFieldWithRender(
                0,
                <div style="text-align: right">
                  {renderSaveButton({
                    save: async function () {
                      const res = await rpc(
                        routes.execReenrollmentsSpec,
                        undefined,
                        {
                          reenrollments,
                          action,
                          earliest_planning_date:
                            earliest_planning_date?.firstday || null,
                          ...queryParams,
                        },
                        { timeoutMS: 45000 },
                      );
                      savedS.set(res);
                      return { tag: "success" };
                    },
                    text: {
                      normal: "Voer uit",
                      success: "Gelukt!",
                    },
                    reload,
                  })}
                </div>,
              );
            },
          )
          .build({
            render: (fields) =>
              selectedN === 0 ? (
                <span></span>
              ) : (
                <div
                  className={style.cancel_main_margins}
                  style={`position: sticky;
                          bottom: 0px;
                          margin-top: 32px;
                          padding-top: 16px;
                          border-top: 1px solid var(--grey-300);
                          box-shadow: var(--box-shadow-large);`}
                >
                  <article style="display: flex; align-items: center; ">
                    <div>
                      <b>
                        {"Je hebt %n inschrijvingen geselecteerd.".replace("%n", selectedN.toString())}
                      </b>{" "}
                      <span>{"Wat wil je er mee doen?"}</span>
                    </div>

                    <div style="flex: 1;"></div>

                    <div>
                      {dyn(actionFormExpandedS, (expanded) => (
                        <div>
                          <button
                            onclick={() => actionFormExpandedS.set(!expanded)}
                            className={style.text_with_icon}
                          >
                            <div>
                              {expanded ? "Verberg" : "Toon"}
                            </div>
                            <i
                              className={
                                style.icon_on_right + FA.icon_arrow_right
                              }
                              style={`transform: rotate(${
                                expanded ? 90 : 270
                              }deg);`}
                            ></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </article>
                  {renderIf(actionFormExpandedS, () => (
                    <div className={style.panel}>
                      <article>
                        {fields.actionChoice()}
                        {fields.actionChoiceInformational()}
                        {fields.action()}
                      </article>
                      <section>
                        {fields.earliest_planning_date_choice()}
                        {fields.earliest_planning_date()}
                      </section>
                      {fields.saveButton()}
                    </div>
                  ))}
                </div>
              ),
          });
      },
    )
    .build({
      render: (fields) => {
        return (
          <main style="max-width: unset; padding-bottom: 0px;">
            <section
              className={style.equal_columns + style.cancel_main_margins}
            >
              {fields.query()}
              {fields.teacher_()}
              {fields.instrument_()}
              {fields.weekday_()}
            </section>
            <div>
              {fields.reenrollments()}

              {fields.action()}

              {dyn(savedS, (saved) => {
                if (saved === null) {
                  return <span></span>;
                } else {
                  return (
                    <div className={style.panel}>
                      <article>
                        <a href={routes.reenrollment_batchesRoute.link()}>
                          {"Bekijk deze groep herinschrijvingen steeds hier"}
                        </a>
                      </article>
                      <label>
                        {"Resultaat" +
                          wrapInParentheses("emails in wachtrij gezet")}
                      </label>
                      {saved
                        .orderBy((s) => renderFirstNameLastName(s.student))
                        .map((s) => (
                          <div>
                            <a
                              href={
                                s.created.tag === "request"
                                  ? routes.enrollment_requestRoute.link({
                                      enrollmentrequestid: s.created.id,
                                    })
                                  : routes.lessongroup_generalRoute.link({
                                      lessongroupid: s.created.id,
                                    })
                              }
                            >
                              {renderFirstNameLastName(s.student)}
                            </a>
                          </div>
                        ))}
                    </div>
                  );
                }
              })}
            </div>
          </main>
        );
      },
    });

  return form.render();
}

function renderLessongroupCell(
  r: reenrollment_poss_from_server,
  opts: {
    allInstruments: { description: string; id: instrumentid }[];
    isTrialLessonExtension: boolean;
  },
) {
  return (
    <a
      target="_blank"
      style="text-decoration: none"
      href={routes.lessongroup_generalRoute.link({
        lessongroupid: r.lessongroup.id,
      })}
    >
      <div className={style.text_with_icon}>
        <i className={FA.icon_glasses}></i>
        <div>{renderFirstNameLastName(r.teacher)}</div>
      </div>
      <div className={style.text_with_icon}>
        <i className={FA.icon_music_solid}></i>
        <div>
          {opts.allInstruments.find((i) => i.id === r.lessongroup.instrumentid)
            ?.description || ""}
        </div>
      </div>
      <div className={style.text_with_icon}>
        <i className={FA.icon_clock_regular}></i>
        <div>
          {renderTimePlusWeekday(
            r.lessongroup.time,
            parseWeekday(r.lessongroup.weekday),
          )}
        </div>
      </div>

      <div className={style.text_with_icon}>
        <i className={FA.icon_calendar_alt_regular}></i>
        <div>
          {ddMMM(r.lessongroup.firstlesson!) +
            " - " +
            ddMMM(r.lessongroup.lastlesson)}
        </div>
      </div>

      <div className={style.text_with_icon}>
        <i className={FA.icon_clipboard_solid}></i>
        <div>{r.formula.description}</div>
      </div>

      {opts.isTrialLessonExtension ? (
        <div className={style.text_with_icon}>
          <i className={FA.icon_info_solid}></i>
          <div>{"Verlenging proeflessen"}</div>
        </div>
      ) : (
        []
      )}
    </a>
  );
}

function renderStatus(
  enrollmentidsS: Source<enrollmentid[]>,
  r: GetReturnTypeFromApiSpec<typeof routes.queryReenrollmentsSpec>[number],
  reload: () => void,
) {
  if (
    r.status.tag === "SlotTaken" ||
    r.status.tag === "OpenForReenrollment" ||
    r.status.tag === "AvailableGrouplesson"
  ) {
    return dyn(enrollmentidsS, (enrollmentids) => {
      const checked = enrollmentids.includes(r.id);

      const slotTakenLink =
        r.status.tag === "SlotTaken" ? (
          r.status.by.tag === "custom" ? (
            <div>{r.status.by.message}</div>
          ) : r.status.by.tag === "lessongroup" ? (
            <div>
              <a
                target="_blank"
                style={`color: ${purple.primary}; font-weight: 600;`}
                href={routes.planningRoute.link({
                  time: r.status.by.time,
                  teacher: r.teacher.internalteacherid,
                  date: r.status.by.firstlesson,
                })}
              >
                {"Uur ingenomen"}
              </a>
            </div>
          ) : (
            checkAllCasesHandled(r.status.by)
          )
        ) : (
          <span></span>
        );

      return [
        slotTakenLink,
        <label
          className={style.input_like + style.checkbox}
          style="margin: 0px;"
        >
          <input
            type="checkbox"
            checked={checked}
            style=""
            onclick={() => toggle(enrollmentidsS, r)}
          />
          <span>{"Selecteer"}</span>
        </label>,
      ];
    });
  } else if (r.status.tag === "EnrollmentrequestFound") {
    const [text, colors] = renderEnrollmentrequestStatus(r.status.status);
    const link =
      r.status.status === "InvitationER" ||
      r.status.status === "Reserved" ||
      r.status.status === "WaitingForApprovalER"
        ? routes.enrollment_requestRoute.link({
            enrollmentrequestid: r.status.enrollmentrequestid,
          })
        : null;
    const st = `color: ${colors.primary}; font-weight: 600;`;
    return link ? (
      <a target="_blank" href={link} style={st}>
        {text}
      </a>
    ) : (
      <span style={st}>{text}</span>
    );
  } else if (r.status.tag === "EnrollmentFound") {
    return (
      <a
        target="_blank"
        style={`color: ${green.primary}; font-weight: 600;`}
        href={routes.lessongroup_generalRoute.link({
          lessongroupid: r.status.lessongroupid,
        })}
      >
        {"Ingeschreven"}
      </a>
    );
  } else if (r.status.tag === "AlreadyHasLessonsNextPeriod") {
    return (
      <a
        target="_blank"
        style={`color: ${green.primary}; font-weight: 600;`}
        href={routes.lessongroup_lessondataRoute.link({
          lessongroupid: r.lessongroup.id,
          selecteddate: r.lessongroup.lastlesson,
          monthsinfuture: undefined,
        })}
      >
        {"Heeft reeds lessen in het volgende schooljaar"}
      </a>
    );
  } else if (r.status.tag === "ManuallyTaggedAsNotToBeReenrolled") {
    return (
      <div>
        <span
          style={`color: black; font-weight: 600; display: inline-block; margin-right: 16px;`}
        >
          {"Wordt niet verlengd" +
            (r.status.reason ? ": " + r.status.reason : "")}
        </span>
        {renderSaveButton({
          save: () =>
            rpc(routes.cancelNoReenrollmentSpec, undefined, {
              enrollmentid: r.id,
            }),
          text: {
            normal: "Annuleer",
          },
          smallbuttons: true,
          reload,
          icon: FA.icon_undo_solid,
        })}
      </div>
    );
  } else if (
    r.status.tag === "FlexPlanning" ||
    r.status.tag === "CustomPlanning"
  ) {
    return (
      <span style={`color: ${red.primary}; font-weight: 600;`}>
        {"Manueel ingeplande reeks, wordt niet verlengd"}
      </span>
    );
  } else {
    return checkAllCasesHandled(r.status);
  }
}

function toggle(
  enrollmentidsS: Source<enrollmentid[]>,
  r: reenrollment_poss_from_server,
) {
  const curr = enrollmentidsS.get();
  const newEnrollmentids = curr.includes(r.id)
    ? curr.filter((enrid) => enrid !== r.id)
    : curr.concat(r.id);
  enrollmentidsS.set(newEnrollmentids);
}

function renderReenrollments(
  reload: () => void,
  parsingS: Source<Parsing<lessongroup_reenrollments[]>>,
  groupedReenrollments: reenrollmentGroup[],
  opts: {
    mode: "daterange" | "end_of_period";
    nextPeriod: null | period;
    allInstruments: { description: string; id: instrumentid }[];
    allInternalTeachers: {
      id: teacherid;
      firstname: string;
      lastname: string;
      internalteacherid: internalteacherid;
      instruments: { id: instrumentid; description: string }[] | null;
    }[];
  },
) {
  const reenrollmentsFlat = groupedReenrollments.flatMap((l) => l.fromServer);
  const tableHeader = (
    <tr>
      <th>{"Student"}</th>
      <th>{"Inschrijving"}</th>
      <th style="max-width: 320px">
        <div style="display: flex;">
          <div>{"Status" + " "}</div>
          <div style="display: flex; flex-direction: column; margin-left: 8px;">
            <a onclick={() => selectAll(groupedReenrollments, "open")}>
              {wrapInParentheses("Selecteer alle")}
            </a>
            {reenrollmentsFlat.some(
              (reenr) => reenr.status.tag === "SlotTaken",
            ) ? (
              <a onclick={() => selectAll(groupedReenrollments, "slot_taken")}>
                {wrapInParentheses(
                  "Selecteer alle met ingenomen tijdstippen",
                )}
              </a>
            ) : (
              []
            )}
            <a onclick={() => selectAll(groupedReenrollments, "clear")}>
              {wrapInParentheses("Geen selectie")}
            </a>
          </div>
        </div>
      </th>
    </tr>
  );
  return (
    <div style="min-height: 100vh; margin-bottom: 128px;">
      <div className={style.panel}>
        <table>
          {tableHeader}
          {groupedReenrollments.flatMap((l, i) =>
            renderLessongroupRow(
              reload,
              opts,
              parsingS,
              l.fromServer,
              l.form,
              l.selectedEnrIds,
              l.isTrialLessonExtension,
              i,
            ),
          )}
        </table>
      </div>
    </div>
  );
}

function renderLessongroupRow(
  reload: () => void,
  opts: {
    mode: "daterange" | "end_of_period";
    allInstruments: { description: string; id: instrumentid }[];
    nextPeriod: null | period;
    allInternalTeachers: {
      id: teacherid;
      firstname: string;
      lastname: string;
      internalteacherid: internalteacherid;
      instruments: { id: instrumentid; description: string }[] | null;
    }[];
  },
  parsingS: Source<Parsing<lessongroup_reenrollments[]>>,
  reenrollmentsForLg: reenrollment_poss_from_server[],
  form: ReturnType<typeof makeLessongroupEditForm>,
  enrollmentids: Source<enrollmentid[]>,
  isTrialLessonExtension: boolean,
  i: number,
) {
  const editingS = new Source(false);

  scheduleForCleanup(
    // Switching off editing when we have no enrollments of this LG selected
    // We're doing it like this because we don't want to watch parsingS
    // in the section that renders the form
    parsingS.observe((parsing) => {
      if (parsing.tag !== "parsed") {
        return;
      }
      const lgSelected = parsing.parsed.find(
        (lg) => lg.lessongroupid === reenrollmentsForLg[0].lessongroup.id,
      );
      if (!lgSelected && editingS.get() === true) {
        editingS.set(false);
      }
    }),
  );

  function renderOtherRelevantEnrollments(
    nextPeriod: null | period,
    student: { firstname: string },
    enrs: {
      lessongroupid: lessongroupid;
      weekday: import("../../../sql/classy/types").weekday;
      time: joda.LocalTime;
      instrument_description: string;
      teacher: { firstname: string; lastname: string };
    }[],
  ): JSX.Child {
    const showS = new Source(false);
    return nextPeriod !== null && enrs.length > 0
      ? dyn(showS, (show) => {
          if (show === true) {
            return (
              <div
                className={
                  style.panel + style.card + style.card_white + style.slide_in
                }
              >
                <div>
                  {"Andere inschrijvingen van %studentname in %period"
                    .replace("%studentname", student.firstname)
                    .replace(
                      "%period",
                      renderPeriod(nextPeriod.firstday, nextPeriod.lastday),
                    )}
                </div>
                {enrs.map((e) => (
                  <div>
                    <a
                      href={routes.lessongroup_generalRoute.link({
                        lessongroupid: e.lessongroupid,
                      })}
                    >
                      {renderTeacherPlusTimePlusWeekday(
                        e.teacher,
                        e.time,
                        parseWeekday(e.weekday),
                      )}
                    </a>
                  </div>
                ))}
              </div>
            );
          } else {
            return (
              <div className={style.text_with_icon}>
                <i
                  className={FA.icon_arrow_right}
                  style="transform: rotate(90deg);"
                ></i>
                <div>
                  <a onclick={() => showS.set(true)}>
                    {" "}
                    Toon andere inschrijvingen
                  </a>
                </div>
              </div>
            );
          }
        })
      : [];
  }

  return reenrollmentsForLg
    .orderBy((r) => renderFirstNameLastName(r.student))
    .map((r, j) => (
      <tr className={i % 2 ? "" : style.bg_grey}>
        {j === 0 ? (
          <td rowSpan={reenrollmentsForLg.length}>
            {renderLessongroupCell(r, { ...opts, isTrialLessonExtension })}
          </td>
        ) : (
          []
        )}

        <td>
          <div>
            <a
              target="_blank"
              href={routes.studentOverviewRoute.link({
                studentid: r.student.id,
              })}
            >
              <div>{renderFirstNameLastName(r.student)}</div>
              {r.status.tag === "AvailableGrouplesson" &&
              r.status.isTrialLessonExtension === true ? (
                <div>{wrapInParentheses("Verlenging proefles")}</div>
              ) : (
                []
              )}
            </a>
          </div>
          {match(r.status.tag)
            .with("EnrollmentFound", () => [])
            .otherwise(() =>
              renderOtherRelevantEnrollments(
                opts.nextPeriod,
                r.student,
                r.otherRelevantEnrollmentsInNextPeriod || [],
              ),
            )}
        </td>

        <td>{renderStatus(enrollmentids, r, reload)}</td>

        {j === 0 ? (
          <td rowSpan={reenrollmentsForLg.length}>{form.render()}</td>
        ) : (
          []
        )}
      </tr>
    ));
}

function renderReenrollmentsSummary(
  allReenrollmentsFiltered: reenrollment_poss_from_server[],
) {
  const groupedByStatus = groupByIntoArrays(
    allReenrollmentsFiltered,
    (r) => r.status.tag,
  );

  const statusses = mapPartial(groupedByStatus, ([status, enrs]) => {
    const green = "green";
    const red = "red";
    const orange = "darkorange";
    const statusStuff = match(status)
      .with("EnrollmentFound", () => ({
        icon: <i className={FA.icon_check_solid}></i>,
        text: "waar een inschrijving voor is aangemaakt",
        order: 1,
        color: green,
      }))
      .with("EnrollmentrequestFound", () => ({
        icon: <i className={FA.icon_check_solid}></i>,
        text: "waar een reservatie of uitnodiging voor is aangemaakt",
        order: 2,
        color: green,
      }))
      .with(P.union("OpenForReenrollment", "AvailableGrouplesson"), () => ({
        icon: <i className={FA.icon_undo_solid}></i>,
        text: "klaar om opnieuw in te schrijven",
        order: 6,
        color: orange,
      }))
      .with("ManuallyTaggedAsNotToBeReenrolled", () => ({
        icon: <i className={FA.icon_check_solid}></i>,
        text: "gemarkeerd als niet opnieuw in te schrijven",
        order: 3,
        color: green,
      }))
      .with("AlreadyHasLessonsNextPeriod", () => ({
        icon: <i className={FA.icon_check_solid}></i>,
        text: "die reeds lessen in het volgende schooljaar hebben",
        order: 4,
        color: green,
      }))
      .with("CustomPlanning", "FlexPlanning", () => ({
        icon: <i className={FA.icon_calendar_alt_regular}></i>,
        text: "met manueel ingeplande lessenreeksen",
        order: 5,
        color: green,
      }))
      .with("SlotTaken", () => ({
        icon: <i className={FA.icon_exclamation_circle_solid}></i>,
        text: "waarvan hun tijdstip is ingenomen",
        order: 7,
        color: red,
      }))
      .exhaustive();

    if (enrs.length !== 0) {
      return { ...statusStuff, count: enrs.length };
    } else {
      return null;
    }
  }).orderBy((s) => s.order);

  return (
    <div className={style.card + style.card_large + style.panel}>
      <article>
        <b>
          {"We vonden %n inschrijvingen".replace(
            "%n",
            allReenrollmentsFiltered.length.toString(),
          )}
        </b>
      </article>
      <ul>
        {statusses.map((st) => (
          <li className={style.text_with_icon} style={`color: ${st.color};`}>
            {st.icon}
            <div>
              <b>{st.count}</b>
              {" " + st.text}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function reenrollmentIsSelectable(
  r: reenrollment_poss_from_server["status"]["tag"],
): r is "SlotTaken" | "OpenForReenrollment" | "AvailableGrouplesson" {
  return (
    r === "SlotTaken" ||
    r === "OpenForReenrollment" ||
    r === "AvailableGrouplesson"
  );
}

function makeLgEditFormInitialFromServerStruct(
  r: reenrollment_poss_from_server,
  isTrialLessonExtension: boolean,
): Omit<lessongroup_reenrollments, "enrollmentids"> {
  return {
    lessongroupid: r.lessongroup.id,
    time: r.lessongroup.time,
    weekday: parseWeekday(r.lessongroup.weekday),
    teacher: r.teacher,
    instrumentid: r.lessongroup.instrumentid,
    lessonduration: r.lessongroup.lessonduration,
    formulaid: r.lessongroup.formulaid,
    isTrialLessonExtension,
    location: {
      location: r.lessongroup.location,
      roomid: r.lessongroup.roomid,
    },
  };
}

function makeLessongroupEditForm(
  query: query,
  opts: {
    allFormulas: formulaFull[];
    allInstruments: { description: string; id: instrumentid }[];
    allInternalTeachers: {
      id: teacherid;
      firstname: string;
      lastname: string;
      internalteacherid: internalteacherid;
      instruments: { id: instrumentid; description: string }[] | null;
    }[];
  },
  selectedEnrIds: Source<enrollmentid[]>,
  initial: Omit<lessongroup_reenrollments, "enrollmentids">,
): Field<{ values: Omit<lessongroup_reenrollments, "enrollmentids"> }> {
  const containerForm = new Form({})
    .addField("editing", [], async () => {
      const s: Source<Parsing<boolean>> = new Source({
        tag: "parsed",
        parsed: false,
      });
      return {
        s,
        cleanup: () => {},
        render: () =>
          dynVariadic([s, selectedEnrIds], ([editing, enrollmentids]) => {
            if (editing.tag !== "parsed") {
              throw new Error("Impossible");
            }
            if (
              editing.parsed === true ||
              /* Doesn't make sense to be able to edit a
               trial lesson extension since the LG is already fixed */
              initial.isTrialLessonExtension === true ||
              enrollmentids.length === 0
            ) {
              return [];
            } else {
              return (
                <button
                  className={
                    style.button +
                    style.button_round +
                    style.button_has_only_icon
                  }
                  onclick={() => s.set({ tag: "parsed", parsed: true })}
                >
                  <i className={FA.icon_pencil_alt_solid}></i>
                </button>
              );
            }
          }),
      };
    })
    .addField("values", ["editing"], async ({ editing }) => {
      if (editing === false) {
        return constantField(initial);
      } else {
        const form = new Form({})
          .addField("lessongroupid", [], () =>
            constantField(initial.lessongroupid),
          )
          .addField("isTrialLessonExtension", [], () =>
            constantField(initial.isTrialLessonExtension),
          )
          .addField("teacher", [], () =>
            selectBox({
              label: "Docent",
              values: opts.allInternalTeachers,
              initial: opts.allInternalTeachers.find(
                (it) =>
                  it.internalteacherid === initial.teacher.internalteacherid,
              ),
              show: renderFirstNameLastName,
            }),
          )
          .addField("instrument", ["teacher"], ({ teacher }) => {
            const values = teacher.instruments || [];
            return selectBox({
              label: "Cursus",
              values,
              initial: values.find((v) => v.id === initial.instrumentid),
              show: (i) => i.description,
            });
          })
          .addField("instrumentid", ["instrument"], ({ instrument }) =>
            constantField(instrument.id),
          )
          .addField("lessonduration", [], () =>
            constantField(initial.lessonduration),
          )
          .addField("weekday", [], () => {
            const values = allWeekdays;
            return selectBox({
              label: "Weekdag",
              values,
              initial: values.find((v) => v.equals(initial.weekday)),
              show: translateWeekday,
            });
          })
          .addField("time", [], () =>
            timeBox({ initialVal: initial.time, label: "Tijdstip" }),
          )
          .addField(
            "location",
            ["time", "teacher", "instrument"],
            ({ time, teacher, instrument }) =>
              locationField(
                query.tag === "daterange"
                  ? query.dates.lastday.plusDays(8)
                  : query.ending_period.lastday,
                time,
                { lessonduration: initial.lessonduration },
                { teacher: { internalteacherid: teacher.internalteacherid } },
                { id: instrument.id },
                {
                  location: initial.location.location,
                  roomId: initial.location.roomid,
                },
                null,
                rpc,
              ),
          )
          .addField(
            "formula",
            ["time", "teacher", "instrument", "weekday"],
            ({ time, teacher, instrument, weekday }) => {
              return formulaField(
                {
                  teacher,
                  weekday,
                  formulas: opts.allFormulas,
                  limitBy: { tag: "slots", slots: [] },
                },
                time,
                instrument,
                opts.allFormulas.find((f) => f.id === initial.formulaid) ||
                  null,
              );
            },
          )
          .addField("formulaid", ["formula"], ({ formula }) =>
            constantField(formula.id),
          )
          .build({
            render: (fields) => (
              <div>
                <div className={style.equal_columns}>
                  {fields.teacher()}
                  {fields.instrument()}
                </div>
                <div className={style.equal_columns}>
                  {fields.weekday()}
                  {fields.time()}
                </div>
                {fields.formula()}
                {fields.location()}
              </div>
            ),
          });
        return form;
      }
    })
    .build();

  return containerForm;
}

function selectAll(
  groupedReenrollments: reenrollmentGroup[],
  mode: "open" | "slot_taken" | "clear",
) {
  for (const group of groupedReenrollments) {
    group.selectedEnrIds.set(
      mode === "clear"
        ? []
        : mode === "slot_taken" || mode === "open"
        ? (() => {
            const statussesToSelect: reenrollment_poss_from_server["status"]["tag"][] =
              mode === "open"
                ? ["OpenForReenrollment", "AvailableGrouplesson"]
                : mode === "slot_taken"
                ? ["SlotTaken"]
                : checkAllCasesHandled(mode);
            return group.fromServer
              .filter(
                (r) =>
                  reenrollmentIsSelectable(r.status.tag) &&
                  statussesToSelect.includes(r.status.tag),
              )
              .map((r) => r.id);
          })()
        : checkAllCasesHandled(mode),
    );
  }
}
